/**
 * SnippetPlayer Hook
 * Manages 10-second preview playback for non-authenticated users
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const SNIPPET_DURATION = 10; // seconds

export const useSnippetPlayer = (isAuthenticated) => {
  const [isSnippetMode, setIsSnippetMode] = useState(!isAuthenticated);
  const [snippetTimeLeft, setSnippetTimeLeft] = useState(SNIPPET_DURATION);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [currentSnippetTrack, setCurrentSnippetTrack] = useState(null);
  const snippetTimerRef = useRef(null);
  const snippetAudioRef = useRef(new Audio());

  // Update snippet mode when auth changes
  useEffect(() => {
    setIsSnippetMode(!isAuthenticated);
    if (isAuthenticated) {
      setShowSignupPrompt(false);
      clearInterval(snippetTimerRef.current);
    }
  }, [isAuthenticated]);

  // Play snippet (10 seconds only)
  const playSnippet = useCallback((track) => {
    if (!track?.audio_url) return;

    const audio = snippetAudioRef.current;
    
    // Stop any existing playback
    audio.pause();
    clearInterval(snippetTimerRef.current);
    
    // Reset state
    setSnippetTimeLeft(SNIPPET_DURATION);
    setCurrentSnippetTrack(track);
    setShowSignupPrompt(false);
    
    // Load and play
    audio.src = track.audio_url;
    audio.currentTime = 0;
    
    audio.play().then(() => {
      // Start countdown timer
      snippetTimerRef.current = setInterval(() => {
        setSnippetTimeLeft(prev => {
          if (prev <= 1) {
            // Time's up - stop and show signup
            audio.pause();
            clearInterval(snippetTimerRef.current);
            setShowSignupPrompt(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }).catch(err => {
      console.error('Snippet play failed:', err);
    });
  }, []);

  // Stop snippet
  const stopSnippet = useCallback(() => {
    snippetAudioRef.current.pause();
    clearInterval(snippetTimerRef.current);
    setCurrentSnippetTrack(null);
    setSnippetTimeLeft(SNIPPET_DURATION);
  }, []);

  // Dismiss signup prompt (user chose not to sign up)
  const dismissSignupPrompt = useCallback(() => {
    setShowSignupPrompt(false);
    setCurrentSnippetTrack(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(snippetTimerRef.current);
      snippetAudioRef.current.pause();
    };
  }, []);

  return {
    isSnippetMode,
    snippetTimeLeft,
    showSignupPrompt,
    currentSnippetTrack,
    playSnippet,
    stopSnippet,
    dismissSignupPrompt,
    SNIPPET_DURATION
  };
};

export default useSnippetPlayer;
